import {BaseElementAsync, BASIS, MsgLabel, OSkinableInit} from "back/commons/basis";
import {IReg, REG} from "lib/commons/registry";
import {IView} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IWspUiEnv, Wsp} from "lib/wsp/wsp";
import {ISearchCrit, ISearchFunc, ISearchPersist, ISearchStore, ISearchViewResult, SearchAnd, SearchRequest, StreamedSearch} from "lib/wsp/search";
import {OSrcGridInit, SrcGrid} from "back/wsp/widgets/srcGrid";
import {buildCritAreaFromXml, IS_UiCritWidget, IUiCritArea, UiCritBool} from "back/wsp/widgets/search/uiCrit";
import {ItemCritFullText, ItemCritFullWsp, ItemCritInSpace, ItemCritLinks} from "back/wsp/widgets/search/itemCrit";
import {ActionBtn, Button} from "back/commons/widgets/buttons";
import {AppHeader, OAppHeaderInit} from "back/core/widgets/appHeader";
import {InfoBrokerBasic} from "lib/commons/infos";
import {ItemViewerSingle} from "back/wsp/views/itemViewerSingle";
import {IItemViewer} from "back/wsp/views/itemMain";
import {InfoFocusItem, ITEM} from "lib/wsp/item";
import {SRC, srcUri} from "lib/wsp/src";
import {POPUP} from "back/commons/widgets/popups";
import {LANG} from "lib/commons/lang";
import {IWedSearchCoordinatorPointer, WedSearchCoordinator} from "back/edit/wed/features/searchBar";
import {ERROR} from "lib/core/errorReport";
import {Action} from "lib/commons/actions";
import {ExportResultCsv_item, ExportResultCsv_task, ExportResultItems} from "back/wsp/actions/searchesActions";
import "back/wsp/dialogs/dialogs_Perms";
import {JSearchResultL10nMsgs} from "back/wsp/views/searches";
import {FORMS} from "lib/commons/forms";


export interface OSearchItemsInit extends OSkinableInit {
	reg?: IReg<IWspUiEnv>

	/** Critères disponibles */
	criterions?: Dict<IUiCritArea>;

	/** Requête courante à éditer */
	initSearchRequest?: ISearchPersist | Element

	/** Mode readonly : les critères de recherche ne peuvent pas être édités
	 * 		FIXME : affichage de l'abstract en attendant de faire évoluer l'API crtérions pour prendre en conmpte le caractère "readonly"
	 * */
	readonly?: boolean

	/** Capacité d'édition du titre de la requête
	 * 	Si false, le champ est masqué. True par défaut
	 **/
	canEditTitle?: boolean

	/** Si initSearchRequest est null, construction à partir d'un item ou d'un espace de départ. */
	fromSrcUri?: srcUri

	/** Si initSearchRequest est null et fullTextByDefault=true, ajoute un 1er critère de recherche de type "fullText". */
	fullTextByDefault?: boolean

	/** Si initSearchRequest, fromSrcUri et fullTextByDefault sont null, construction personnalisée des critères affichées par défaut. */
	buildCrit?: (searchItems: SearchItems) => void

	/** Fonction de modification de la requête avant exécution (ex : ajout de critères forcés non visibles coté UI, ...) */
	searchWhereWrapper?: (where: Array<ISearchCrit>, reg: IReg<IWspUiEnv>) => Promise<void>

	/** Nombre d'items maximum recherchés initialement */
	firstMax?: number

	/** Nombre d'items maximum recherchés aprés avoir demandé "tout afficher" */
	fullMax?: number;

	/** Affiche le panel de la mainView. */
	showMainView?: boolean

	/** Affiche l'état de la recherche et les actions de raffraichissement (true par défaut) */
	showResultBar?: boolean

	/** Init de la liste des résultats. */
	srcGrid?: OSrcGridInit

	/** Init du appHeader. */
	appHeader?: OAppHeaderInit<SearchItems>

	/**
	 * Système de stockage des requêtes associé à initSearchRequest. Si spécifié, ajoute un bouton :
	 * - d'enregistrement si initSearchRequest.id est renseigné.
	 * - création de requête sinon.
	 */
	searchStore?: ISearchStore

	/* Permet de contextualiser les messages UI */
	msgs?: JSearchResultL10nMsgs
}

export interface SearchItems extends BaseElementAsync, IView, ISearchViewResult {
	initialize(init: OSearchItemsInit): this
}

export class SearchItems extends BaseElementAsync implements IView, ISearchViewResult {

	reg: IReg<IWspUiEnv>;

	criterions: Dict<IUiCritArea>;

	/** Fonction de modification de la requête avant exécution (ex : ajout de critères forcés non visibles coté UI, ...) */
	searchWhereWrapper: (searchCrits: ISearchCrit[], reg: IReg<IWspUiEnv>) => Promise<void>;

	firstMax = 30;

	fullMax = 2000;

	currentMax: number;

	searchStore?: ISearchStore;

	initSearchRequest?: ISearchPersist;

	get wsp(): Wsp {return this.reg.env.wsp};

	/** Eléments UI */
	grid: SrcGrid;
	critRoot: HTMLElement;
	critAbstract: HTMLDivElement;
	mainView: IItemViewer;
	countDisplay: MsgLabel;
	searchMoreBtn: Button;

	streamedSearch: StreamedSearch;

	protected _conf: OSearchItemsInit;

	protected _searchColumns: Map<string, string | ISearchFunc>;

	protected _startReqPending: Promise<void>;

	protected _lastReq: SearchRequest;

	public static APPBAR_MAIN_ACTIONS = "actions:searchItems:bar";

	get readonly(): boolean {return this._conf.readonly || false}

	async doSearch(max: number) {
		if (!this._searchColumns) {
			this._searchColumns = new Map();
			this.grid.fillSearchColumns(this._searchColumns);
		}
		const req = this._lastReq = new SearchRequest(this._searchColumns).setMax(max + 1);
		const where = await this.buildWhere();
		if (!where?.toDom()?.hasChildNodes()) {
			this.countDisplay?.setCustomMsg("Veuillez préciser la recherche", "info");
			this.grid.srcGridDatas.setDatas([]);
			return;
		}
		req.setWhere(where);

		try {
			while (this._startReqPending) await this._startReqPending; //raceCond : 2 appels très proches avant que le 1er this.streamedSearch.search(req) réponde.
		} finally {
			this._startReqPending = null;
		}
		if (this._lastReq !== req) return;// raceCond optimisation: une autre requête a été lancée on abandonne
		this.currentMax = max;
		this.grid.srcGridDatas.setDatas([]);
		this.countDisplay?.setCustomMsg("En cours...");
		try {
			//console.log("SearchItems.doSearch::::", req.toDom());
			this._startReqPending = this.streamedSearch.search(req)
				.then(() => {this._startReqPending = null})
				.catch((e) => {
					ERROR.log(e);
					this.countDisplay?.setCustomMsg("Requête en échec", "error");
					this._startReqPending = null;
				});
		} catch (e) {
			ERROR.log(e);
			this.countDisplay?.setCustomMsg("Requête en échec", "error");
		}
	}

	async buildWhere(): Promise<ISearchCrit> {
		const where = new SearchAnd();
		if (!this.critRoot) return where;
		for (let ch = DOM.findFirstChild(this.critRoot, IS_UiCritWidget); ch; ch = DOM.findNextSibling(ch, IS_UiCritWidget)) {
			LANG.pushIfDefined(where, ch.buildCrit());
		}
		if (this.searchWhereWrapper)
			await this.searchWhereWrapper(where, this.reg);
		return where;
	}

	buildWhereXml(): Element {
		return JSX.asXml(() => {
			const root = <where/>;
			if (!this.critRoot) return root;
			for (let ch = DOM.findFirstChild(this.critRoot, IS_UiCritWidget); ch; ch = DOM.findNextSibling(ch, IS_UiCritWidget)) {
				const sub = ch.buildUiXmlCrit();
				if (sub) root.appendChild(sub);
			}
			return root;
		});
	}

	async viewSearch(whereCrit: Element, searchId: string | null, searchLabel?: string, searchDescription?: string): Promise<string> {
		await this.initCrits(whereCrit, true);
		return undefined;
	}

	protected async _initialize(init: OSearchItemsInit): Promise<void> {
		this._conf = init;
		const callerReg = this.findReg(init);
		this.searchWhereWrapper = init.searchWhereWrapper;
		if ("firstMax" in init) this.firstMax = init.firstMax;
		if ("fullMax" in init) this.fullMax = init.fullMax;

		if (init.showMainView) {
			//Besoin d'un sous-reg pour un infoBroker dédié
			this.reg = REG.createSubReg(callerReg);
			this.reg.env.infoBroker = new InfoBrokerBasic(); //InfoBroker local à la fenetre de dialogue.
			(this.reg.env as IWedSearchCoordinatorPointer).wedSearchCoord = new WedSearchCoordinator(); //cordinateur local de recherche wed
		} else {
			this.reg = callerReg;
		}
		this.criterions = ("criterions" in init) ? init.criterions : this.reg.getListAsMap("wsp.crit.items");
		this.streamedSearch = new StreamedSearch(this.wsp, (rows: Dict<any>[], done: boolean) => {
			const gridDatas = this.grid.srcGridDatas;
			gridDatas.updateDatas(gridDatas.countRows(), 0, ...rows);
			if (done) {
				let hideShowMore = true;
				const count = gridDatas.countRows();
				const max = this.currentMax;
				if (count > max) {
					this.countDisplay?.setCustomMsg(this._conf?.msgs?.resultsMore ? this._conf.msgs.resultsMore(max) : `Plus de ${max} items trouvés`);
					if (this.currentMax === this.firstMax) hideShowMore = false;
				} else if (count === 0) {
					this.countDisplay?.setCustomMsg(this._conf?.msgs?.resultsEmpty ? this._conf.msgs.resultsEmpty : "Aucun item trouvé");
				} else if (count === 1) {
					this.countDisplay?.setCustomMsg(this._conf?.msgs?.resultsOne ? this._conf.msgs.resultsOne : "1 item trouvé");
				} else {
					this.countDisplay?.setCustomMsg(this._conf?.msgs?.resultsCount ? this._conf.msgs.resultsCount(count) : `${count} items trouvés`);
				}
				if (this.searchMoreBtn) this.searchMoreBtn.hidden = hideShowMore;
			}
		});

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("scroll/small", sr);
		this.reg.installSkin("form-control-areas", sr);
		this._initAndInstallSkin(this.localName, init);
		const editor = sr.appendChild(<div id="critEditor" c-resizable=""/>);
		this.critRoot = editor.appendChild(<div id="critRoot"/>);
		if (this.readonly) {
			DOM.addClass(editor, "abstract");
			this.critAbstract = editor.appendChild(<div id="critAbstract"/>) as HTMLDivElement;
			DOM.setHidden(this.critRoot, true);
		}

		sr.appendChild(<c-resizer c-orient="column"/>);
		if (init.showMainView) {
			const parentMainView = sr.appendChild(<div id="results" c-resizable="">
				<div id="gridCtn" c-resizable="">
					{this.buildResults(init)}
				</div>
				<c-resizer c-orient="row"/>
				<div id="mainView" c-resizable=""/>
			</div>).lastElementChild as HTMLElement;
			this.mainView = new ItemViewerSingle();
			await this.mainView.initViewer(this.reg, parentMainView, null, null);
			this.addEventListener('grid-select', this.onGridSelect);
		} else {
			sr.append(...this.buildResults(init));
		}

		const initHeader: OAppHeaderInit<SearchItems> = BASIS.newInit(Object.assign({
			reg: this.reg,
			focusListening: this.grid,
			actionContext: this,
			mainActions: this.reg.mergeLists(SearchItems.APPBAR_MAIN_ACTIONS),
			mainBar: {
				groupOrder: 'edit raz lib io *'
			},
		} as OAppHeaderInit<SearchItems>, init.appHeader), this.reg);
		sr.insertBefore(new AppHeader().initialize(initHeader), editor);

		await this.initCrits();
		FORMS.findFirstFocusable(this.critRoot)?.focus();

		//intit ok, activation des écoutes de changement des critères
		if (this.critRoot) this.critRoot.addEventListener('wsp-uicrit-change', this.doSearch.bind(this, this.firstMax));
		this.doSearch(this.firstMax);
	}

	protected buildResults(init: OSearchItemsInit) {
		const initGrid = BASIS.newInit(init.srcGrid, this.reg);
		initGrid.hideHeaders = true;
		if (initGrid.skinScroll === undefined) initGrid.skinScroll = 'scroll/small';
		if (init.showMainView) initGrid.itemHandlingReact = this.reg.env.infoBroker;
		this.grid = new SrcGrid().initialize(initGrid);
		this.grid.setAttribute("c-resizable", "");

		const results = [];
		if (init.showResultBar !== false) {
			const resultBar = <div id="resultBar"/>;
			this.countDisplay = resultBar.appendChild(<MsgLabel id="count"/>) as MsgLabel;
			if (this.fullMax > this.firstMax) this.searchMoreBtn = resultBar.appendChild(<Button id="searchMore" ui-context="dialog" hidden="" label="Voir plus" onclick={this.doSearch.bind(this, this.fullMax)}/> as Button);
			resultBar.appendChild(<Button id="relaunch" ui-context="dialog" label="Relancer" title="Relancer la recherche" onclick={this.onRelaunch}/>);
			results.push(resultBar);
		}

		results.push(this.grid);

		if (init.searchStore) {
			//ihm d'enregistrement de la requête.
			this.searchStore = init.searchStore;
			this.initSearchRequest = init.initSearchRequest;
			const searchStoreBar = <div id="searchStore"/>;
			if (init.canEditTitle !== false) {
				const titleElt = <input title="Nom de la requête" id="titleReq" value={this.initSearchRequest?.title || ""}/> as HTMLInputElement;
				if (this.readonly) titleElt.readOnly = true;
				searchStoreBar.append(<span id="titleReqCtn"><h3>Nom de la requête</h3>{titleElt}</span>,);
			}
			if (!this.readonly) searchStoreBar.appendChild(<Button id="saveReq" ui-context="dialog" label="Enregistrer" title="Enregistrer cette requête" icon="/@skin@/wsp/views/searches/searchesBtn.svg" onclick={this.onSave}/>);
			results.push(searchStoreBar);
		}
		return results;
	}

	/** (re)Initialisation de la zone de recherche
	 * @param definedWhereCrit : critère définis à afficher (on bypasse alors la conf initiale)
	 */
	public async initCrits(definedWhereCrit?: Element, doSearch = false) {
		let whereCrit: Element = definedWhereCrit || this.getInitWhereCrit();
		if (this.critRoot) this.critRoot.innerText = "";
		if (this.critRoot && whereCrit) {
			for (let ch = whereCrit.firstElementChild; ch; ch = ch.nextElementSibling) {
				const critArea = buildCritAreaFromXml(ch, this.criterions);
				if (critArea) {
					const crit = await critArea.loadBody(this);
					if (crit) this.critRoot.appendChild(await crit.initialize(this).initFromXmlCrit(ch));
				}
			}
		} else if (this._conf.buildCrit) {
			this._conf.buildCrit(this);
		} else if (this.critRoot) {
			this.critRoot.appendChild(<h3>Portée</h3>);
			if (this._conf.fromSrcUri) {
				if (ITEM.getSrcUriType(this._conf.fromSrcUri) === 'space') {
					const crit = this.critRoot.appendChild(ItemCritInSpace.AREA.buildBody(this)) as ItemCritInSpace;
					crit.spaceUri = this._conf.fromSrcUri;
				} else {
					const crit = this.critRoot.appendChild(ItemCritLinks.AREA_DESC.buildBody(this)) as ItemCritLinks;
					crit.itemPtr.setSrcRef(this._conf.fromSrcUri);
				}
			} else {
				this.critRoot.appendChild(ItemCritFullWsp.AREA.buildBody(this));
			}
			this.critRoot.appendChild(<h3>Critères</h3>);
			const rootFilters = this.critRoot.appendChild(UiCritBool.AREA_AND_ROOT.buildBody(this)) as UiCritBool;
			if (this._conf.fullTextByDefault) rootFilters.insertCrit(ItemCritFullText.AREA.buildBody(this));
		}
		if (this.critAbstract && whereCrit) {
			this.critAbstract.textContent = "";
			const desc = UiCritBool.AREA_AND_ROOT.buildAbstract(whereCrit, this);
			if (desc) this.critAbstract.appendChild(desc);
		}
		if (doSearch) await this.doSearch(this.currentMax);
	}

	protected getInitWhereCrit(): Element | null {
		const initReq = this._conf.initSearchRequest;
		if (!initReq) return null
		if (initReq instanceof Element) return initReq;
		if (initReq.whereCrit) return initReq.whereCrit instanceof Element ? initReq.whereCrit : initReq.whereCrit(this);
		return null
	}

	protected showTimer: any;

	protected onGridSelect(ev: Event) {
		if (this.showTimer) clearTimeout(this.showTimer);
		if (this.grid.countSelectedRows() === 1) {
			this.showTimer = setTimeout(() => {
				const sd = this.grid.shortDescs[0];
				if (sd) {
					(this.reg.env as IWedSearchCoordinatorPointer).wedSearchCoord.setParamsOnce(UiCritBool.AREA_AND_ROOT.buildWedSearch(this.buildWhereXml(), this.criterions));
					this.reg.env.infoBroker.dispatchInfo(new InfoFocusItem(SRC.srcRef(sd), sd), this);
				}
			}, 500);
		}
	}

	protected onRelaunch(this: Button, ev: Event) {
		const me = DOMSH.findHost<SearchItems>(this);
		me.doSearch(me.firstMax);
	}

	protected onSave(this: Button, ev: Event) {
		const me = DOMSH.findHost<SearchItems>(this);
		const search = Object.create(me.initSearchRequest || null) as ISearchPersist;
		const inputTi = me.shadowRoot.getElementById('titleReq') as HTMLInputElement;
		search.title = inputTi ? inputTi.value : undefined;
		search.whereCrit = me.buildWhereXml();
		me.searchStore.persistSearch(search);
		POPUP.findPopupableParent(me).close();
	}

	protected onClose(this: Button, ev: Event) {
		const me = DOMSH.findHost<SearchItems>(this);
		POPUP.findPopupableParent(me).close();
	}

	onViewShown(): void {
		this.mainView?.onViewShown();
	}

	onViewHidden(closed?: boolean): void {
		if (closed) {
			if (this.showTimer) clearTimeout(this.showTimer);
			this.streamedSearch?.close();
		}
		this.mainView?.onViewHidden(closed);
	}
}

REG.reg.registerSkin('wsp-search-item', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	c-appheader {
		border-bottom: 1px solid var(--border-color);
	}

	#critEditor {
		flex: .8 .2 5em;
		display: flex;
		min-height: 0;
		min-width: 0;
		padding: 2px;
	}

	#critEditor.abstract {
		flex: 0.2;
		text-align: center;
		align-items: center;
	}

	#critAbstract {
		flex: auto;
		font-size: var(--label-size);
	}

	.boolOp, .boolBracket {
		color: var(--alt1-color);
	}

	.critVal {
		font-weight: bold;
	}

	#mainView {
		flex: 1 1 50%;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	c-resizer[c-orient=row] {
		width: 1px;
	}

	c-resizer[c-orient=column] {
		height: 2px;
	}

	#critRoot {
		flex: 1;
		overflow: auto;
		padding-inline-start: .6em;
	}

	h3 {
		font-style: italic;
		font-size: 1em;
		color: var(--alt1-color);
		margin: 0;
		margin-inline-start: -.4em;
		user-select: none;
	}

	#results {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1 1 20em;
	}

	#gridCtn {
		flex: 1 1 20em;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	wsp-src-grid {
		flex: 1 1 20em;
		border-inline: none;
	}

	#resultBar {
		display: flex;
		min-height: fit-content;
		min-width: 0;
		margin: 2px;
		align-items: center;
		justify-content: center;
		flex-wrap: wrap;
	}
	
	#titleReqCtn {
		flex: 1;
	  display: flex;
	  min-height: fit-content;
	  min-width: 0;
	  flex-direction: column;
	  margin-inline-start: .6em;
	  margin-block-end: .5em;
  }

	#titleReq {
		flex: 1;
		margin-inline-start: 2px;
	}

	#searchStore {
		display: flex;
		align-items: flex-end;
	  justify-content: flex-end;
	}

	#count {
		flex: 0 1 auto;
		font-style: italic;
		margin: 2px;
		justify-content: start;
	}
`);

customElements.define("wsp-search-item", SearchItems);

/** Réinitialisation de la requête dans son état initial lors de l'ouverture de la fenêtre */
class ResetRequest extends Action<SearchItems> {

	static SINGLETON = new ResetRequest();

	constructor() {
		super("resetRequest");
		this._label = "Réinitialiser le formulaire de recherche";
		this._icon = "/@skin@/commons/icons/reset.svg";
		this._group = 'raz';
	}

	isVisible(ctx: SearchItems): boolean {
		if (ctx?.readonly) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: SearchItems, ev?: Event) {
		return ctx.initCrits();
	}

}

REG.reg.addToList("actions:searchItems:bar", "resetRequest", 1, ResetRequest.SINGLETON);

/** Copie d'une requête dans le presse papier */
class CopyRequest extends Action<SearchItems> {
	static SINGLETON = new CopyRequest();

	constructor() {
		super("copyRequest");
		this._label = "Copier la requête";
		this._description = "Copier la requête dans le presse-papier";
		this._icon = "/@skin@/commons/icons/copy.svg";
		this._group = 'edit';
	}

	/* Tjs enabled avec les critères cachés...
	isEnabled(ctx: SearchItems): boolean {
		const where = ctx.buildWhereXml();
		if(!where?.hasChildNodes()) return false;
		return super.isEnabled(ctx);
	}
	*/

	async execute(ctx: SearchItems, ev?: Event) {
		try {
			const where = ctx.buildWhereXml();
			await navigator.clipboard.writeText(DOM.serializer().serializeToString(where));
		} catch (e) {
			POPUP.showNotifForbidden("Copie dans le presse-papier impossible. Vérifiez dans votre navigateur que vous avez autorisé ce site à accéder au presse-papier.", ctx);
		}
	}

}

REG.reg.addToList("actions:searchItems:bar", "copyRequest", 1, CopyRequest.SINGLETON);

/** Colle une requête depuis le presse papier */
class PasteRequest extends Action<SearchItems> {
	static SINGLETON = new PasteRequest();

	constructor() {
		super("pasteRequest");
		this._label = "Coller la requête";
		this._description = "Coller la requête issue du presse-papier";
		this._icon = "/@skin@/commons/icons/paste.svg";
		this._group = 'edit';
	}

	isVisible(ctx: SearchItems): boolean {
		if (ctx?.readonly) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: SearchItems, ev?: Event) {
		let ct: string;
		try {
			ct = await navigator.clipboard.readText();
		} catch (e) {
			POPUP.showNotifForbidden("Impossible de lire dans le presse-papier. Vérifiez dans votre navigateur que vous avez autorisé ce site à accéder au presse-papier.", ctx);
			return;
		}

		const where = ct ? DOM.parseDomValid(`${ct}`, null, "text/xml") : null;
		if (where?.documentElement?.nodeName === 'where')
			return ctx.initCrits(where.documentElement, true);
		else
			POPUP.showNotifInfo("Aucune requête n'a pu être reconnue", ctx);
	}

}

REG.reg.addToList("actions:searchItems:bar", "pasteRequest", 1, PasteRequest.SINGLETON);


// Actions spécifiques "items"
REG.reg.addToList("actions:searchItems:bar:item", "exportResultCsv", 1, new ExportResultCsv_item()
	.requireVisiblePerm("ui.searchItems.export.props")
	.setLabel("csv")
	.override('initButtonNode', (buttonNode: ActionBtn<any>, ctx: any) => {
		buttonNode._hideLabel = false;
	})
);
REG.reg.addToList("actions:searchItems:bar:item", "exportResultItems", 1, new ExportResultItems()
	.requireVisiblePerm("ui.searchItems.export.items")
	.setLabel("scwsp")
	.override('initButtonNode', (buttonNode: ActionBtn<any>, ctx: any) => {
		buttonNode._hideLabel = false;
	})
);

// Actions spécifiques "taches"
REG.reg.addToList("actions:searchItems:bar:task", "exportResultCsv", 1, new ExportResultCsv_task()
	.requireVisiblePerm("ui.searchItems.export.props")
	.setLabel("csv")
	.override('initButtonNode', (buttonNode: ActionBtn<any>, ctx: any) => {
		buttonNode._hideLabel = false;
	})
);